import { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { FormInput, FormSelect, FormUpload, Button, Modal, EligCheck, OtherDocsUpload } from 'components';
import useConfigOptions from 'hooks/useConfigOptions';
import { preCheckLoanEligibility } from "../../misc/loanPreChecks";
import { getDateAfterTimeframe, nextMonthStartDate } from 'components/misc/functions';
import { useDispatch } from 'react-redux';
import { show } from 'providers/toastSlice';
import LoanRequirement from './LoanRequirement';
import ReviewModal from 'components/Utils/ReviewModal';

function SectionCard({ number, title, children }) {
    return (
        <div className="col-span-full">
            <div className="rounded-xl border border-gray-200 dark:border-white/[.08] bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
                <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100 dark:border-white/[.06] bg-gray-50/50 dark:bg-white/[.02]">
                    <span className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center text-xs font-bold text-amber-700 dark:text-amber-400 shrink-0">
                        {number}
                    </span>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{title}</h3>
                </div>
                <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {children}
                </div>
            </div>
        </div>
    );
}

function SalariedAdvanced({ client }) {

    const dispatch = useDispatch();
    const {
        sectorOpts, purposeOfLoanOpts, loanAmtLimits,
        optionsYN, numOfYearsWithEmployer, missedPaymentOptions,
        paymentPlanOpts, paymentDurationOpts, automaticPaymentOpts,
        relationshipManagers,
    } = useConfigOptions();
    const fullName = client?.otherNames + " " + client?.surname;

    const [isOpen, setIsOpen] = useState(false);
    const [isLoanReqOpen, setIsLoanReqOpen] = useState(false);
    const [reviewModalOpen, setReviewModalOpen] = useState(false);

    const closeModal = () => {
        setIsOpen(false)
    }
    
    const openModal = () => {
        setIsOpen(true)
    }

    const [employedCurrently, setEmployedCurrently] = useState('');
    const [salaryStatus, setSalaryStatus] = useState('');
    const [incomeAmt, setIncomeAmt] = useState('');
    const [debtStatus, setDebtStatus] = useState('');
    const [debtAmt, setDebtAmt] = useState(debtStatus === "Yes" ? '' : 0);
    const [employerName, setEmployerName] = useState('');
    const [yearsAtWork, setYearsAtWork] = useState('');
    const [employmentSector, setEmploymentSector] = useState('');
    const [outstandingLoans, setOutstandingLoans] = useState('');
    const [loansTaken, setLoansTaken] = useState('');
    const [loanDefaultStatus, setLoanDefaultStatus] = useState('');
    const [defaultedLoanStatus, setDefaultedLoanStatus] = useState('');
    const [amountReq, setAmountReq] = useState(''); // set this to 70% of 'incomeAmt'
    const [purpose, setPurpose] = useState('');
    const [paymentPlan, setPaymentPlan] = useState('');
    const [paymentDuration, setPaymentDuration] = useState('');
    const [autoDeduction, setAutoDeduction] = useState('');
    // const [loanKind, setLoanKind] = useState('');
    const [incomeProof, setIncomeProof] = useState();
    const [bankStatement, setBankStatement] = useState();
    const [momoStatement, setMomoStatement] = useState();
    const [employmentProof, setEmploymentProof] = useState();
    const [otherDocs, setOtherDocs] = useState([]);
    const [startDate, setStartDate] = useState(getDateAfterTimeframe('daily'));
    const [relManager, setRelManager] = useState('');
    const [guarantorFirstName, setGuarantorFirstName] = useState('');
    const [guarantorLastName, setGuarantorLastName] = useState('');
    const [guarantorPhone, setGuarantorPhone] = useState('');
    const [guarantorAddress, setGuarantorAddress] = useState('');
    const [guarantorRelationship, setGuarantorRelationship] = useState('');
    const guarantorIdType = 'GhanaCard';
    const [guarantorIdNumber, setGuarantorIdNumber] = useState('');
    const [guarantorDigitalAddress, setGuarantorDigitalAddress] = useState('');
    const [guarantorDob, setGuarantorDob] = useState('');
    const signedName = fullName;
    const [processing, setProcessing] = useState(false);
    const [preCheckData, setPreCheckData] = useState({});

    const handleRequest = async(e) => {
        e.preventDefault();
        setProcessing(true);

        if(amountReq > loanAmtLimits.salariedAdvanceLoanLimit){
            dispatch(show({
                state : true,
                message : `Loan amount requested exceeds the limit of GHS${loanAmtLimits.salariedAdvanceLoanLimit}`,
                type : "error"
            }))
            setProcessing(false);
            return;
        }

        // check to see if files have been uploaded
        let incomeFile = document.getElementById("incomeProof");
        let bankFile = document.getElementById("bankStatement");
        let momoFile = document.getElementById("momoStatement");
        let employmentFile = document.getElementById("employmentProof");

        if(!incomeFile.files.length > 0){
            setProcessing(false);
            dispatch(show({
                type : "error",
                state : true,
                message : "Must attach proof of income"
            }))
            return;
        }

        if(!bankFile.files.length > 0){
            setProcessing(false);
            dispatch(show({
                type : "error",
                state : true,
                message : "Must attach 3 months bank statement"
            }))
            return;
        }

        if(!momoFile.files.length > 0){
            setProcessing(false);
            dispatch(show({
                type : "error",
                state : true,
                message : "Must attach 3 months momo statement"
            }))
            return;
        }

        if(!employmentFile.files.length > 0){
            setProcessing(false);
            dispatch(show({
                type : "error",
                state : true,
                message : "Must attach proof of employment of guarantor"
            }))
            return;
        }

        let data = { employedCurrently, salaryStatus, incomeAmt, debtAmt, employerName, yearsAtWork, employmentSector, outstandingLoans, loansTaken, loanDefaultStatus, defaultedLoanStatus, amountReq, purpose, paymentPlan, paymentDuration, autoDeduction, incomeProof, bankStatement, momoStatement, employmentProof, otherDocs, startDate, relManager, guarantorFirstName, guarantorLastName, guarantorPhone, guarantorAddress, guarantorRelationship, guarantorIdType, guarantorIdNumber, guarantorDigitalAddress, guarantorDob, signedName };

        // precheck loan application eligibility
        let response = await preCheckLoanEligibility("salaried-advance", data);
        setPreCheckData({ ...response, data });

        setTimeout(() => {
            setProcessing(false);
            openModal();
        }, 2000);
    }

    useEffect(() => {
        setIsLoanReqOpen(true);
    }, []);

    return (
        <>
        <LoanRequirement 
            isOpen={isLoanReqOpen}
            setIsLoanReqOpen={setIsLoanReqOpen}
            type="Salaried Advance"
            requirements={[
                "3 Months Bank Statement / Payslip",
                "3 Months Mobile Money Statement",
                "Proof of Employment of Guarantor (Employment Letter / Recent Payslip / Valid Staff ID)",
                "Ghana Card Details (image included) and GPS Address of Guarantor",
            ]}
        />
        <div className='display_card'>
            <form className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-2' encType={'multipart/form-data'} onSubmit={handleRequest}>

                <SectionCard number="1" title="Income Information">
                    <FormSelect 
                        label='Are you employed with your current employer for at least 1 year?'
                        name='employedCurrently'
                        id="employedCurrently"
                        content={optionsYN}
                        value={employedCurrently}
                        setValue={setEmployedCurrently}
                        required={true}
                    />
                    
                    <FormSelect 
                        label='Is your regular monthly income equal to or greater than GHS2,000 per month?'
                        name='salaryStatus'
                        id="salaryStatus"
                        content={optionsYN}
                        value={salaryStatus}
                        setValue={setSalaryStatus}
                        required={true}
                    />

                    <FormInput 
                        label='Provide your monthly regular income'
                        name='incomeAmt'
                        id="incomeAmt"
                        type='number'
                        value={incomeAmt}
                        setValue={setIncomeAmt}
                        required={true}
                    />

                    <FormSelect 
                        label='Are you currently paying any loans(hire purchase payments)?'
                        name='debtStatus'
                        id="debtStatus"
                        content={optionsYN}
                        value={debtStatus}
                        setValue={setDebtStatus}
                        required={true}
                    />

                    {debtStatus === "Yes" && (
                    <FormInput 
                        label='How much is your debt?'
                        name='debtAmt'
                        id="debtAmt"
                        type='number'
                        value={debtAmt}
                        setValue={setDebtAmt}
                    />
                    )}
                </SectionCard>

                <SectionCard number="2" title="Scoring Information">
                    <FormInput 
                        label='Name of your employer (Company)?'
                        name='employerName'
                        id="employerName"
                        type='text'
                        value={employerName}
                        setValue={setEmployerName}
                        required={true}
                    />

                    <FormSelect 
                        label='Kindly state the number of years you worked with your current employer?'
                        name='yearsAtWork'
                        id="yearsAtWork"
                        content={numOfYearsWithEmployer}
                        value={yearsAtWork}
                        setValue={setYearsAtWork}
                        required={true}
                    />

                    <FormSelect 
                        label='Which of the following categories of sector does your employment fall under?'
                        name='employmentSector'
                        id="employmentSector"
                        content={sectorOpts}
                        value={employmentSector}
                        setValue={setEmploymentSector}
                        required={true}
                    />
                </SectionCard>

                <SectionCard number="3" title="Credit Behaviour Information">
                    <FormSelect 
                        label='Do you currently have any outstanding debts or loans?'
                        name='outstandingLoans'
                        id="outstandingLoans"
                        content={optionsYN}
                        value={outstandingLoans}
                        setValue={setOutstandingLoans}
                        required={true}
                    />

                    <FormSelect 
                        label='Have you taken a loan from any financial institution?'
                        name='loansTaken'
                        id="loansTaken"
                        content={optionsYN}
                        value={loansTaken}
                        setValue={setLoansTaken}
                        required={true}
                    />

                    <FormSelect 
                        label='Have you ever defaulted on or missed a loan payment?'
                        name='loanDefaultStatus'
                        id="loanDefaultStatus"
                        content={optionsYN}
                        value={loanDefaultStatus}
                        setValue={setLoanDefaultStatus}
                        required={true}
                    />

                    <FormSelect 
                        label='How many times did you miss payment on your loan?'
                        name='defaultedLoanStatus'
                        id="defaultedLoanStatus"
                        content={missedPaymentOptions}
                        value={defaultedLoanStatus}
                        setValue={setDefaultedLoanStatus}
                        required={true}
                    />
                </SectionCard>

                <SectionCard number="4" title="Loan Information">
                    <FormInput 
                        label='Loan amount'
                        name='amountReq'
                        id="amountReq"
                        type='number'
                        value={amountReq}
                        setValue={setAmountReq}
                        required={true}
                    />

                    <FormSelect 
                        label='What is the purpose of the loan?'
                        name='purpose'
                        id="purpose"
                        content={purposeOfLoanOpts}
                        value={purpose}
                        setValue={setPurpose}
                        required={true}
                    />

                    <FormSelect 
                        label='What is the desired payment plan?'
                        name='paymentPlan'
                        id="paymentPlan"
                        content={paymentPlanOpts.slice(1,4)}
                        value={paymentPlan}
                        setValue={setPaymentPlan}
                        required={true}
                    />

                    <FormSelect 
                        label='What is the desired payment duration?'
                        name='paymentDuration'
                        id="paymentDuration"
                        content={paymentDurationOpts.slice(0, 3)}
                        value={paymentDuration}
                        setValue={setPaymentDuration}
                        required={true}
                    />

                    <FormSelect 
                        label='Select your preferred automatic repayment method'
                        name='autoDeduction'
                        id="autoDeduction"
                        content={automaticPaymentOpts}
                        value={autoDeduction}
                        setValue={setAutoDeduction}
                        required={true}
                    />

                    <div>
                        <label htmlFor="startDate" className='form-label'>
                            Select loan start date
                        </label>
                        <input 
                            type="date"
                            name={"startDate"}
                            id={"startDate"}
                            className="form-input disabled:opacity-75 disabled:bg-gray-200 placeholder:text-gray-700 disabled:cursor-not-allowed"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            max={nextMonthStartDate()}
                            required={true}
                        />
                    </div>

                    <FormSelect 
                        label='Kindly select a relationship manager'
                        name='relManager'
                        id="relManager"
                        content={relationshipManagers}
                        value={relManager}
                        setValue={setRelManager}
                        required={true}
                    />
                </SectionCard>

                <SectionCard number="5" title="Guarantor Information">
                    <FormInput 
                        label="Guarantor's first name"
                        name='guarantorFirstName'
                        id="guarantorFirstName"
                        type='text'
                        value={guarantorFirstName}
                        setValue={setGuarantorFirstName}
                        required={true}
                    />

                    <FormInput 
                        label="Guarantor's last name"
                        name='guarantorLastName'
                        id="guarantorLastName"
                        type='text'
                        value={guarantorLastName}
                        setValue={setGuarantorLastName}
                        required={true}
                    />

                    <FormInput 
                        label="Guarantor's phone number"
                        name='guarantorPhone'
                        id="guarantorPhone"
                        type='text'
                        value={guarantorPhone}
                        setValue={setGuarantorPhone}
                        required={true}
                    />

                    <FormInput 
                        label="Guarantor's address"
                        name='guarantorAddress'
                        id="guarantorAddress"
                        type='text'
                        value={guarantorAddress}
                        setValue={setGuarantorAddress}
                        required={true}
                    />

                    <FormInput 
                        label="Your relationship to guarantor"
                        name='guarantorRelationship'
                        id="guarantorRelationship"
                        type='text'
                        value={guarantorRelationship}
                        setValue={setGuarantorRelationship}
                        required={true}
                    />

                    <FormInput 
                        label="Guarantor's GhanaCard number"
                        name='guarantorIdNumber'
                        id="guarantorIdNumber"
                        type='text'
                        value={guarantorIdNumber}
                        setValue={setGuarantorIdNumber}
                        required={true}
                    />

                    <FormInput 
                        label="Guarantor's digital address"
                        name='guarantorDigitalAddress'
                        id="guarantorDigitalAddress"
                        type='text'
                        value={guarantorDigitalAddress}
                        setValue={setGuarantorDigitalAddress}
                        required={true}
                    />

                    <div>
                        <label htmlFor="guarantorDob" className='form-label'>
                            Guarantor's date of birth
                        </label>
                        <input 
                            type="date"
                            name={"guarantorDob"}
                            id={"guarantorDob"}
                            className="form-input disabled:opacity-75 disabled:bg-gray-200 placeholder:text-gray-700 disabled:cursor-not-allowed"
                            value={guarantorDob}
                            onChange={e => setGuarantorDob(e.target.value)}
                            min={"1930-01-01"}
                            max={new Date().toISOString().split('T')[0]}
                            required={true}
                        />
                    </div>
                </SectionCard>

                <SectionCard number="6" title="Files to upload (Mandatory**)">
                    <FormUpload 
                        label='Upload proof of income'
                        name='incomeProof'
                        id="incomeProof"
                        setValue={setIncomeProof}
                        required={true}
                    />

                    <FormUpload 
                        label='Upload bank statement'
                        name='bankStatement'
                        id="bankStatement"
                        setValue={setBankStatement}
                        required={true}
                    />

                    <FormUpload 
                        label='Upload momo statement'
                        name='momoStatement'
                        id="momoStatement"
                        setValue={setMomoStatement}
                        required={true}
                    />

                    <FormUpload 
                        label='Proof of Employment of Guarantor (Employment Letter / Recent Payslip / Valid Staff ID)'
                        name='employmentProof'
                        id="employmentProof"
                        setValue={setEmploymentProof}
                        required={true}
                    />

                    <OtherDocsUpload 
                        setValue={setOtherDocs}
                    />
                </SectionCard>                

                <div className="col-span-full rounded-xl border border-blue-200 dark:border-blue-500/20 bg-blue-50 dark:bg-blue-500/5 px-6 py-5 space-y-4">
                    <div>
                        <h4 className="text-sm font-semibold text-blue-800 dark:text-gray-300 mb-1.5">Loan Authorization</h4>
                        <p className="text-xs text-blue-700 dark:text-gray-300 leading-relaxed">
                            I, hereby apply for loans stated in Sections of this application. The information I have provided here is to the best of my knowledge true and correct. I authorize CedisPay Micro-Credit Enterprise to verify the correctness of this information and also to obtain additional information if it deems necessary in evaluating my loan application.
                        </p>
                    </div>
                    <div>
                        <h4 className="text-sm font-semibold text-blue-800 dark:text-gray-300 mb-1.5">Disclosure to Credit Reference Bureaus</h4>
                        <p className="text-xs text-blue-700 dark:text-gray-300 leading-relaxed">
                            CedisPay will obtain information about you from the credit reference bureaus to check your credit status and identity. The bureaus will record our enquiries which may be seen by other institutions that make their own credit enquiries about you. CedisPay shall also disclose your credit transactions to credit reference bureaus in accordance with the <strong>Credit Reporting Regulations, 2020 (L.I 2394)</strong>.
                        </p>
                    </div>
                    <FormInput label="Signed Name" name="signedName" id="signedName" type="text" value={signedName} disabled={true} />
                </div>

                <div className="col-span-full flex items-center gap-3 pt-2">
                    <Button 
                        btnText={"Check eligibility"}
                        btnType={"submit"}
                        btnClasses={"application-button w-max"}
                        processing={processing}
                    />
                </div>

            </form>
        </div>

        <Modal isOpen={isOpen} closeModal={closeModal}>
            <Dialog.Panel className="w-full max-w-md md:max-w-3xl transform overflow-hidden rounded-2xl bg-white dark:bg-dark-bg p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-white/[.7] flex items-center mb-4"
                >
                    Pre-Check Status: <span className={`ml-1 px-3 py-1.5 text-white font-medium rounded-full text-xs capitalize ${preCheckData?.status === 'accepted' ? 'bg-emerald-400' : preCheckData?.status === 'rejected' ? 'bg-red-400' : ''}`}>{preCheckData?.status}</span>
                </Dialog.Title>
                <EligCheck 
                    status={preCheckData?.status}
                    data={preCheckData}
                    loanType={"salaried-advance"}
                    closeModal={closeModal}
                    setReviewModalOpen={setReviewModalOpen}
                />
            </Dialog.Panel>
        </Modal>

        <ReviewModal 
            isOpen={reviewModalOpen} 
            isClose={setReviewModalOpen}
            client={signedName}
        />
        </>
    )
}

export default SalariedAdvanced